import { Checkbox, FormControl, FormControlLabel, MenuItem, Select } from '@mui/material';
import DatePicker from 'react-datepicker';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import CheckIcon from '../../Assets/Images/icon-checkbox-checked.png';
import UncheckIcon from '../../Assets/Images/icon-checkbox-unchecked.png';
import Popup from '../../Commons/Components/Popup';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import './index.scss';

const Term = ({ title, message }) => (
  <div className="enrolment_term">
    <h1 id="enrolment_term-message-receive-title" className="page-title">
      {title}
    </h1>
    <br />
    <br />
    <div id="enrolment_term-message-receive-instruction" className="page-body">
      <p id="enrolment_term-message-receive-instruction-paragraph" dangerouslySetInnerHTML={{ __html: message }}></p>
    </div>
  </div>
);

const View = ({
  isLoading,
  lecture,
  onClickToggleAgreementTerms,
  agreementTerms,
  onClickPayment,
  formatPhoneNumber,
  thirdPartyQuestionFieldList,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const userInfo = useSelector((state) => state.user.userInfo) || {};
  const [useUserInfo, setUseUserInfo] = useState(false);
  const [selectedEducationValue, setSelectedEducationValue] = useState('');
  const thirdPartyQuestionFields = useMemo(
    () => (lecture ? lecture.ThirdPartyClassQuestions.map((question) => question.question_field) : []),
    [lecture],
  );
  const questionFieldList = useMemo(
    () => (thirdPartyQuestionFieldList ? Object.values(thirdPartyQuestionFieldList) : []),
    [thirdPartyQuestionFieldList],
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isValid },
    control,
    watch,
  } = useForm({
    defaultValues: {},
    mode: 'all', // 입력 변경시 유효성 검사를 수행
  });

  const getField = (fieldKey) => questionFieldList.find((f) => f.field === fieldKey);
  const isFieldActive = (fieldKey) => thirdPartyQuestionFields.includes(fieldKey);

  const handleChangeEducation = (event) => {
    setSelectedEducationValue(event.target.value);
  };

  const getValidationMsg = () => {
    if (errors?.user_name) {
      return '* 이름을 정확히 입력해주세요.';
    } else if (errors?.user_phone) {
      return '* 연락처를 정확히 입력해주세요';
    } else if (errors?.user_email) {
      return '* 이메일 주소를 정확히 입력해주세요';
    } else if (errors?.user_birth) {
      return '* 생년월일을 정확히 입력해주세요';
    } else if (errors?.user_graduation_year) {
      return '* 졸업년도를 정확히 입력해주세요';
    } else if (errors?.user_graduation_month) {
      return '* 졸업 월을 선택해주세요';
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (!isLoading && lecture) {
      setValue('lecture_title', lecture.title);
    }
  }, [isLoading]);

  useEffect(() => setValue('agreement', agreementTerms), [agreementTerms]);
  useEffect(() => {
    if (useUserInfo) {
      setValue('user_name', userInfo.name, { shouldValidate: true });
      setValue('user_phone', formatPhoneNumber(userInfo.phone), { shouldValidate: true });
      setValue('user_email', userInfo.email, { shouldValidate: true });
    } else {
      setValue('user_name', '', { shouldValidate: true });
      setValue('user_phone', '', { shouldValidate: true });
      setValue('user_email', '', { shouldValidate: true });
    }
  }, [useUserInfo]);

  useEffect(() => {
    const year = watch('user_birth_year');
    const month = watch('user_birth_month');
    const day = watch('user_birth_day');

    if (year && month && day) {
      setValue('user_birth', `${year}-${month}-${day}`, { shouldValidate: true });
    } else {
      setValue('user_birth', null, { shouldValidate: true });
    }
  }, [watch('user_birth_year'), watch('user_birth_month'), watch('user_birth_day')]);

  if (isLoading) {
    return null;
  }

  return (
    <div className="enrolment_wrap">
      <div className="inner_wrap">
        <div className="header">
          <div className="enrolment_form-title">교육신청</div>
        </div>
        <div className="content-wrap">
          <div className="enrolment_loggedInUserInfo_wrap">
            <h3>회원정보</h3>
            <ul className="form-fields-wrap">
              <li>
                <label>이름</label>
                <input type="text" defaultValue={userInfo.name} readOnly={true} tabIndex={-1} />
              </li>
              <li>
                <label>연락처</label>
                <input
                  type="tel"
                  defaultValue={userInfo.phone && formatPhoneNumber(userInfo.phone)}
                  readOnly={true}
                  tabIndex={-1}
                />
              </li>
              <li>
                <label>이메일</label>
                <input type="email" defaultValue={userInfo.email} readOnly={true} tabIndex={-1} />
              </li>
            </ul>
          </div>
          <div className="enrolment_form_wrap">
            <div className="enrolment_form_header">
              <div className="title_wrap">
                <h3>신청정보</h3>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      disableRipple
                      sx={{ padding: 0 }}
                      onClick={(e) => setUseUserInfo(e.target?.checked)}
                    />
                  }
                  label="회원정보와 동일"
                  sx={{
                    fontSize: '0.75rem',
                    color: 'darkgray',
                    marginLeft: 1,
                  }}
                />
              </div>
              <div className="message_wrap">
                * 수강 안내 및 교육이수증 발급에 필요한 정보이니 모든 항목을 정확하게 입력해 주세요.
              </div>
            </div>

            <ul className="form-fields-wrap">
              <li>
                <label>강의명</label>
                <input
                  type="text"
                  readOnly
                  {...register('lecture_title', {
                    required: true,
                  })}
                />
              </li>

              {isFieldActive('user_name') && (
                <li className={isMobile && errors.user_name ? 'error-list' : ''}>
                  <label htmlFor="user_name">
                    {getField('user_name').label} {!getField('user_name').is_optional && '*'}
                  </label>
                  <div className="form-fields-content-wrap">
                    <input
                      id="user_name"
                      type="text"
                      placeholder="본인의 이름을 작성해 주세요."
                      {...register('user_name', {
                        required: !getField('user_name').is_optional,
                        pattern: /^[가-힣a-zA-Z]{2,}$/,
                        value: null,
                      })}
                      readOnly={useUserInfo}
                    />
                    {!isMobile && (
                      <span className="form-fields--error fields--error-comment">
                        {errors.user_name ? '* 이름을 정확히 입력해주세요' : ''}
                      </span>
                    )}
                  </div>
                </li>
              )}

              {isFieldActive('user_phone') && (
                <li className={isMobile && errors.user_phone ? 'error-list' : ''}>
                  <label htmlFor="user_phone">
                    {getField('user_phone').label} {!getField('user_phone').is_optional && '*'}
                  </label>
                  <div className="form-fields-content-wrap">
                    <input
                      id="user_phone"
                      type="tel"
                      placeholder="'-' 없이 숫자만 입력해 주세요."
                      {...register('user_phone', {
                        required: !getField('user_phone').is_optional,
                        pattern: /^[0-9]{2,3}-?[0-9]{3,4}-?[0-9]{4}$/i,
                        value: null,
                      })}
                      readOnly={useUserInfo}
                      onBlur={(e) => {
                        setValue('user_phone', formatPhoneNumber(e.target.value), { shouldValidate: true });
                      }}
                    />
                    {!isMobile && (
                      <span className="form-fields--error fields--error-comment">
                        {errors.user_phone ? '* 연락처를 정확히 입력해주세요' : ''}
                      </span>
                    )}
                  </div>
                </li>
              )}

              {isFieldActive('user_email') && (
                <li className={isMobile && errors.user_email ? 'error-list' : ''}>
                  <label htmlFor="user_email">
                    {getField('user_email').label} {!getField('user_email').is_optional && '*'}
                  </label>
                  <div className="form-fields-content-wrap">
                    <input
                      id="user_email"
                      type="text"
                      placeholder="예) enterjobedu@k-lab.me"
                      {...register('user_email', {
                        required: !getField('user_email').is_optional,
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        value: null,
                      })}
                      readOnly={useUserInfo}
                    />
                    {!isMobile && (
                      <span className="form-fields--error fields--error-comment">
                        {errors.user_email ? '* 이메일 주소를 정확히 입력해주세요' : ''}
                      </span>
                    )}
                  </div>
                </li>
              )}

              {isFieldActive('user_birth') && (
                <li className={isMobile && errors.user_birth ? 'error-list' : ''}>
                  <label htmlFor="user_birth" style={{ marginBottom: '0.5rem', display: 'block' }}>
                    {getField('user_birth').label} {!getField('user_birth').is_optional && '*'}
                  </label>
                  <div className="form-fields-content-wrap">
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: 'auto' }}>
                      {/* Year Select */}
                      <FormControl variant="standard" sx={{ minWidth: 60, maxWidth: 140, flex: 1 }}>
                        <Select
                          {...register('user_birth_year', {
                            required: true,
                          })}
                          displayEmpty
                          defaultValue=""
                          sx={{ fontSize: '0.875rem', height: '1.7rem' }}
                        >
                          <MenuItem value="" disabled>
                            연도
                          </MenuItem>
                          {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Month Select */}
                      <FormControl variant="standard" sx={{ minWidth: 60, maxWidth: 140, flex: 1 }}>
                        <Select
                          {...register('user_birth_month', {
                            required: true,
                          })}
                          displayEmpty
                          defaultValue=""
                          sx={{ fontSize: '0.875rem', height: '1.7rem' }}
                        >
                          <MenuItem value="" disabled>
                            월
                          </MenuItem>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                            <MenuItem key={month} value={String(month).padStart(2, '0')}>
                              {month}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Day Select */}
                      <FormControl variant="standard" sx={{ minWidth: 60, maxWidth: 140, flex: 1 }}>
                        <Select
                          {...register('user_birth_day', {
                            required: true,
                          })}
                          displayEmpty
                          defaultValue=""
                          sx={{ fontSize: '0.875rem', height: '1.7rem' }}
                        >
                          <MenuItem value="" disabled>
                            일
                          </MenuItem>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                            <MenuItem key={day} value={String(day).padStart(2, '0')}>
                              {day}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    {!isMobile && (
                      <span
                        className="form-fields--error fields--error-comment"
                        style={{ marginTop: '0.5rem', display: 'block' }}
                      >
                        {errors.user_birth_year || errors.user_birth_month || errors.user_birth_day
                          ? '* 생년월일을 정확히 입력해주세요'
                          : ''}
                      </span>
                    )}
                  </div>
                </li>
              )}

              {isFieldActive('user_gender') && (
                <li style={{ display: 'flex', alignItems: 'center' }}>
                  <label htmlFor="user_gender" style={{ marginRight: '1rem' }}>
                    {getField('user_gender').label} {!getField('user_gender').is_optional && '*'}
                  </label>
                  <div className="form-fields-content-wrap">
                    <FormControl variant="standard" sx={{ minWidth: 120 }}>
                      <Select
                        {...register('user_gender', {
                          required: !getField('user_gender').is_optional,
                          value: null,
                        })}
                        displayEmpty={true}
                        defaultValue=""
                        sx={{ fontSize: '0.875rem', height: '1.7rem' }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: '0.9rem' }}>
                          성별 선택
                        </MenuItem>
                        <MenuItem value="M" sx={{ fontSize: '0.9rem' }}>
                          남
                        </MenuItem>
                        <MenuItem value="F" sx={{ fontSize: '0.9rem' }}>
                          여
                        </MenuItem>
                      </Select>
                    </FormControl>

                    {!isMobile && (
                      <span
                        className="form-fields--error fields--error-comment"
                        style={{ marginTop: '0.5rem', display: 'block' }}
                      >
                        {errors.user_gender ? '* 성별을 정확히 입력해주세요' : ''}
                      </span>
                    )}
                  </div>
                </li>
              )}

              {isFieldActive('user_education') && (
                <li style={{ display: 'flex', alignItems: 'center' }}>
                  <label htmlFor="user_education" style={{ marginRight: '1rem' }}>
                    {getField('user_education').label} {!getField('user_education').is_optional && '*'}
                  </label>
                  <FormControl variant="standard" sx={{ minWidth: 120 }}>
                    <Select
                      {...register('user_education', {
                        required: !getField('user_education').is_optional,
                        value: null,
                      })}
                      onChange={handleChangeEducation}
                      displayEmpty={true}
                      defaultValue=""
                      sx={{ fontSize: '0.875rem', height: '1.7rem' }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: '0.9rem' }}>
                        최종학력 선택
                      </MenuItem>
                      <MenuItem value="고등학교 졸업" sx={{ fontSize: '0.9rem' }}>
                        고등학교 졸업
                      </MenuItem>
                      <MenuItem value="대학교 재학" sx={{ fontSize: '0.9rem' }}>
                        대학교 재학
                      </MenuItem>
                      <MenuItem value="대학교 졸업" sx={{ fontSize: '0.9rem' }}>
                        대학교 졸업
                      </MenuItem>
                      <MenuItem value="대학원 재학" sx={{ fontSize: '0.9rem' }}>
                        대학원 재학
                      </MenuItem>
                      <MenuItem value="대학원 졸업" sx={{ fontSize: '0.9rem' }}>
                        대학원 졸업
                      </MenuItem>
                      <MenuItem value="etc" sx={{ fontSize: '0.9rem' }}>
                        기타
                      </MenuItem>
                    </Select>
                  </FormControl>
                </li>
              )}

              {selectedEducationValue === 'etc' && (
                <li className="user_education_etc__list">
                  <label htmlFor="user_education">
                    <span>
                      <KeyboardReturnIcon
                        style={{ transform: 'scaleX(-1)', fontSize: '1rem', marginRight: '0.1rem' }}
                      />{' '}
                      기타 답변
                    </span>
                  </label>
                  <input {...register('user_education')} type="text" id="user_education" name="user_education" />
                </li>
              )}

              {isFieldActive('user_graduation_year') && (
                <li className="user_graduation_year__list">
                  <label htmlFor="user_graduation_year">
                    {getField('user_graduation_year').label} {!getField('user_graduation_year').is_optional && '*'}
                  </label>
                  <div className="form-fields-content-wrap">
                    <DatePicker
                      {...register('user_graduation_year', {
                        required: !getField('user_graduation_year').is_optional,
                        value: null,
                      })}
                      popperPlacement="bottom"
                      dateFormat="yyyy"
                      showYearPicker
                      selected={watch('user_graduation_year')}
                      onChange={(date) => setValue('user_graduation_year', date)}
                      placeholderText="연도 선택"
                      maxDate={new Date(new Date().getFullYear() + 7, 11, 31)}
                      yearItemNumber={8}
                    />
                    {!isMobile && (
                      <span className="form-fields--error fields--error-comment">
                        {errors.user_graduation_year ? '* 졸업년도를 정확히 입력해주세요' : ''}
                      </span>
                    )}
                  </div>
                </li>
              )}

              {isFieldActive('user_graduation_month') && (
                <li style={{ display: 'flex', alignItems: 'center' }}>
                  <label htmlFor="user_graduation_month" style={{ marginRight: '1rem' }}>
                    {getField('user_graduation_month').label} {!getField('user_graduation_month').is_optional && '*'}
                  </label>
                  <div className="form-fields-content-wrap">
                    <FormControl variant="standard" sx={{ minWidth: 120 }}>
                      <Select
                        {...register('user_graduation_month', {
                          required: !getField('user_graduation_month').is_optional,
                          value: null,
                        })}
                        displayEmpty={true}
                        defaultValue=""
                        sx={{ fontSize: '0.875rem', height: '1.7rem' }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: '0.9rem' }}>
                          월 선택
                        </MenuItem>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                          <MenuItem key={month} value={month} sx={{ fontSize: '0.9rem' }}>
                            {month}월
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!isMobile && (
                      <span className="form-fields--error fields--error-comment">
                        {errors.user_graduation_month ? '* 졸업 월을 선택해주세요' : ''}
                      </span>
                    )}
                  </div>
                </li>
              )}

              {isFieldActive('user_school_name') && (
                <li>
                  <label htmlFor="user_school_name">
                    {getField('user_school_name').label} {!getField('user_school_name').is_optional && '*'}
                  </label>
                  <input
                    {...register('user_school_name', {
                      required: !getField('user_school_name').is_optional,
                      value: null,
                    })}
                    type="text"
                    id="user_school_name"
                    name="user_school_name"
                  />
                </li>
              )}

              {isFieldActive('user_school_department') && (
                <li>
                  <label htmlFor="user_school_department">
                    {getField('user_school_department').label} {!getField('user_school_department').is_optional && '*'}
                  </label>
                  <input
                    {...register('user_school_department', {
                      required: !getField('user_school_department').is_optional,
                      value: null,
                    })}
                    type="text"
                    id="user_school_department"
                    name="user_school_department"
                  />
                </li>
              )}

              {isFieldActive('user_apply_reason') && (
                <li
                  className={
                    isMobile && errors.user_apply_reason
                      ? 'user_apply_reason__list error-list'
                      : 'user_apply_reason__list'
                  }
                  style={{ padding: '0' }}
                >
                  <label htmlFor="user_apply_reason">
                    {getField('user_apply_reason').label} {!getField('user_apply_reason').is_optional && '*'}
                  </label>
                  <textarea
                    {...register('user_apply_reason', {
                      required: !getField('user_apply_reason').is_optional,
                      value: null,
                    })}
                    id="user_apply_reason"
                    name="user_apply_reason"
                    rows="5"
                    style={{ scrollbarWidth: 'thin' }}
                  />
                </li>
              )}

              {isFieldActive('user_pre_question') && (
                <li
                  className={
                    isMobile && errors.user_pre_question
                      ? 'user_pre_question__list error-list'
                      : 'user_pre_question__list'
                  }
                  style={{ padding: '0' }}
                >
                  <label htmlFor="user_pre_question">
                    {getField('user_pre_question').label} {!getField('user_pre_question').is_optional && '*'}
                  </label>
                  <textarea
                    id="user_pre_question"
                    placeholder="수업 내용과 관련하여 질문하고 싶은 내용이 있다면 작성해 주세요."
                    {...register('user_pre_question', {
                      required: !getField('user_pre_question').is_optional,
                      value: null,
                    })}
                    rows="1"
                    style={{ scrollbarWidth: 'thin' }}
                  />
                </li>
              )}

              {isFieldActive('user_recommender') && (
                <li>
                  <label htmlFor="user_recommender">
                    {getField('user_recommender').label} {!getField('user_recommender').is_optional && '*'}
                  </label>
                  <div className="form-fields-content-wrap">
                    <input
                      {...register('user_recommender', {
                        required: !getField('user_recommender').is_optional,
                        value: null,
                      })}
                      type="text"
                      id="user_recommender"
                      name="user_recommender"
                    />
                    {!isMobile && (
                      <span className="form-fields--error fields--error-comment">
                        {errors.user_recommender ? '* 추천인을 정확히 입력해주세요.' : ''}
                      </span>
                    )}
                  </div>
                </li>
              )}
            </ul>

            {isMobile && Object.keys(errors).length > 0 && (
              <div>
                <span className="fields--error-comment">{getValidationMsg()}</span>
              </div>
            )}
          </div>

          <div className="enrolment_terms_box">
            <div className="enrolment_terms">
              <div className="agreement" onClick={onClickToggleAgreementTerms}>
                <span className="checkbox">
                  <img src={agreementTerms ? CheckIcon : UncheckIcon} alt="enrolment_agree_terms" />
                </span>
                <h3>교육정보 확인 및 개인정보 제공 동의</h3>
              </div>
              <ul className="terms_list_box">
                {lecture.ClassTerms.map((term) => (
                  <li key={term.id}>
                    {term.title}
                    <Popup
                      modal
                      trigger={
                        <span>
                          <FormattedMessage id="COMMON_READ_MORE" />
                        </span>
                      }
                    >
                      <Term title={term.title} message={term.content} />
                    </Popup>
                  </li>
                ))}
              </ul>
            </div>

            <input
              type="submit"
              id="enrolment_complete_click"
              className="enrolment_btn"
              onClick={(e) => {
                onClickPayment(getValues());
              }}
              disabled={!agreementTerms || !isValid}
              value="신청하기"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default View;
